"use strict";
// Copyright (c) 2023-2024 The Bitcoin developers
// Distributed under the MIT software license, see the accompanying
// file COPYING or http://www.opensource.org/licenses/mit-license.php.
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.FailoverProxy = exports.ChronikError = void 0;
exports.appendWsUrls = appendWsUrls;
const axios_1 = __importDefault(require("axios"));
const isomorphic_ws_1 = __importDefault(require("isomorphic-ws"));
const proto = __importStar(require("../proto/chronik"));
const WEBSOCKET_TIMEOUT_MS = 5000;
/**
 * Wait before a failed reconnect is tried again. The first reconnect after a
 * disconnect is immediate so a live failover peer is subscribed before the
 * next block. Doubles on each failed attempt.
 */
const WS_RECONNECT_BASE_DELAY_MS = 500;
/** Longest wait between reconnect attempts during an outage. */
const WS_RECONNECT_MAX_DELAY_MS = 30000;
/**
 * Build the ErrorEvent shape `WsConfig.onError` already accepts.
 * `target` may be missing when every endpoint failed before a socket existed.
 */
/**
 * Destroy a socket immediately. `close()` waits for a handshake that a dead
 * Chronik peer never completes, and that wait keeps the process alive.
 */
function terminateWs(socket) {
    const terminator = socket;
    try {
        if (typeof terminator.terminate === 'function') {
            terminator.terminate();
            return;
        }
        socket.close();
    }
    catch {
        // The socket may already be gone.
    }
}
function wsErrorEvent(err, target) {
    const message = err instanceof Error ? err.message : String(err);
    return {
        error: err,
        message,
        type: 'error',
        target: target,
    };
}
/**
 * Send a reconnect failure to onError. Log it when the caller did not
 * register a handler, so the outage is visible without crashing the process.
 */
function emitWsError(wsEndpoint, err) {
    if (wsEndpoint.onError === undefined) {
        console.error('Chronik websocket reconnect failed:', err);
        return;
    }
    try {
        wsEndpoint.onError(wsErrorEvent(err, wsEndpoint.ws));
    }
    catch (callbackErr) {
        console.error('Chronik websocket onError callback failed:', callbackErr);
    }
}
/**
 * Error response decoded from a working Chronik server, e.g. a 404 for an
 * unknown txid or a 400 for a tx that fails validation. `status` is the HTTP
 * status code, so callers can tell user errors (4xx) from server errors (5xx)
 * without parsing `message`. `name` stays 'Error' so `String(err)` is
 * unchanged for existing callers.
 */
class ChronikError extends Error {
    constructor(message, status) {
        super(message);
        this.status = status;
    }
}
exports.ChronikError = ChronikError;
// Converts an array of chronik http/https urls into
// websocket equivalents and combines them into an object array
function appendWsUrls(urls) {
    const combinedUrls = [];
    for (const thisUrl of urls) {
        if (thisUrl.startsWith('https://')) {
            combinedUrls.push({
                url: thisUrl,
                wsUrl: 'wss://' + thisUrl.substring('https://'.length) + '/ws',
            });
        }
        else if (thisUrl.startsWith('http://')) {
            combinedUrls.push({
                url: thisUrl,
                wsUrl: 'ws://' + thisUrl.substring('http://'.length) + '/ws',
            });
        }
        else {
            throw new Error(`Invalid url found in array: ${thisUrl}`);
        }
    }
    return combinedUrls;
}
/**
 * Handles the networking to Chronik `Endpoint`s, including cycling
 * through both types of endpoints.
 */
class FailoverProxy {
    constructor(urls) {
        /** Live-socket handshake timeout. Unit tests shorten this. */
        this._wsOpenTimeoutMs = WEBSOCKET_TIMEOUT_MS;
        /** Probe sockets keyed by the endpoint that started them. */
        this._probes = new Map();
        // Validate url input
        const urlsArray = typeof urls === 'string' ? [urls] : urls;
        if (urlsArray.length === 0) {
            throw new Error('Url array must not be empty');
        }
        // Validate each entry in the url array
        for (const url of urlsArray) {
            if (url.endsWith('/')) {
                throw new Error("`url` cannot end with '/', got: " + url);
            }
            if (!url.startsWith('https://') && !url.startsWith('http://')) {
                throw new Error("`url` must start with 'https://' or 'http://', got: " +
                    url);
            }
        }
        // Initializes _endpointArray with an object Array containing
        // 'url' and 'wsUrl' props
        this._endpointArray = appendWsUrls(urlsArray);
        this._workingIndex = 0;
    }
    // For unit test verification
    getEndpointArray() {
        return this._endpointArray;
    }
    // Derives the endpoint array index based on _workingIndex
    // This is set to public for unit testing purposes
    deriveEndpointIndex(loopIndex) {
        return (this._workingIndex + loopIndex) % this._endpointArray.length;
    }
    // Overriding working index for unit testing purposes
    setWorkingIndex(newIndex) {
        this._workingIndex = newIndex;
    }
    async post(path, data) {
        return this._request(path, 'POST', data);
    }
    async get(path) {
        return this._request(path, 'GET');
    }
    /**
     * Returns a Get or POST axios response from the Chronik indexer
     *
     * @param {string} path the path to the Chronik API endpoint
     * @param {string} requestType the flag indicating whether this is a get or post request
     * @param {string} data the optional axios request data specific to post requests
     * @returns {Uint8Array} returns the axios response data from Chronik
     * @throws {error} throws error on non-responsive server or valid error responses
     */
    async _request(path, requestType, data) {
        // Cycle through known Chronik instances
        for (let i = 0; i < this._endpointArray.length; i += 1) {
            const index = this.deriveEndpointIndex(i);
            const thisProxyUrl = this._endpointArray[index].url;
            try {
                const response = await this._callAxios(thisProxyUrl, path, requestType, data);
                // If no errors thrown in the above call then set this index to state
                this._workingIndex = index;
                return response;
            }
            catch (err) {
                if (err instanceof Error &&
                    ('code' in err ||
                        err
                            .toString()
                            .includes('Unable to decode error msg, chronik server is indexing or in error state') ||
                        // We can see this type of error msg from an indexing server
                        // Observed when a chronik node is down (e.g. bitcoin-cli stop) but
                        // server is reachable, nginx running
                        err.toString().trim().endsWith(':'))) {
                    // Server outage, skip to next url in loop
                    // Connection error msgs have a 'code' key of 'ECONNREFUSED'
                    // Error messages from the chronik server (i.e. error
                    // messages that should not trigger a 'try next server'
                    // attempt) are of the chronik proto Error type, which has no 'code' key
                    continue;
                }
                // Throw upon all other valid error responses from chronik
                throw err;
            }
        }
        // All known Chronik instances have not been responsive
        throw new Error(`Error connecting to known Chronik instances`);
    }
    /**
     * Calls axios for a GET or POST response
     *
     * @param {string} url the singular chronik url
     * @param {string} path the path to the Chronik API endpoint
     * @param {string} requestType the flag indicating whether this is a get or post request
     * @param {string} data the optional axios request data for post requests
     * @returns {Uint8Array} returns the axios response data from Chronik
     * @throws {error} throws error on non-responsive server or valid error responses
     */
    async _callAxios(url, path, requestType, data) {
        let response;
        if (requestType === 'GET') {
            response = await axios_1.default.get(`${url}${path}`, {
                responseType: 'arraybuffer',
                validateStatus: undefined,
            });
        }
        else if (requestType === 'POST') {
            response = await axios_1.default.post(`${url}${path}`, data, {
                responseType: 'arraybuffer',
                validateStatus: undefined,
                // Prevents Axios encoding the Uint8Array as JSON or something
                transformRequest: x => x,
                headers: {
                    'Content-Type': 'application/x-protobuf',
                },
            });
        }
        else {
            throw new Error('Impossible by types');
        }
        // Parse for valid error responses (e.g. txid not found)
        this.ensureResponseErrorThrown(response, path);
        return new Uint8Array(response.data);
    }
    ensureResponseErrorThrown(response, path) {
        if (response.status != 200) {
            let errorCanBeDecoded = false;
            let error;
            try {
                // If we can decode this error with proto, it is an expected chronik error
                // from a working server and we should return it to the user
                error = proto.Error.decode(new Uint8Array(response.data));
                errorCanBeDecoded = true;
            }
            catch {
                // If we can't decode this error with proto, something is wrong with this server instance
                // It may be indexing
                // In this case, we should try the next server
                throw new Error('Unable to decode error msg, chronik server is indexing or in error state');
            }
            if (errorCanBeDecoded) {
                throw new ChronikError(`Failed getting ${path}: ${error.msg}`, response.status);
            }
        }
    }
    _trackProbe(wsEndpoint, testWs) {
        let probes = this._probes.get(wsEndpoint);
        if (probes === undefined) {
            probes = new Set();
            this._probes.set(wsEndpoint, probes);
        }
        probes.add(testWs);
    }
    _untrackProbe(wsEndpoint, testWs) {
        const probes = this._probes.get(wsEndpoint);
        if (probes === undefined) {
            return;
        }
        probes.delete(testWs);
        if (probes.size === 0) {
            this._probes.delete(wsEndpoint);
        }
    }
    /**
     * Drop probe sockets for one endpoint. `close()` and `pause()` call this
     * so an in-flight liveness check cannot keep the process alive.
     */
    discardProbes(wsEndpoint) {
        const probes = this._probes.get(wsEndpoint);
        if (probes === undefined) {
            return;
        }
        this._probes.delete(wsEndpoint);
        for (const probe of probes) {
            terminateWs(probe);
        }
    }
    /** Destroy the live socket, if any. */
    terminateSocket(socket) {
        if (socket !== undefined) {
            terminateWs(socket);
        }
    }
    /**
     * Check if a given websocket URL connects.
     * There is no msg from chronik confirming a connection, and waiting for a
     * block or a transaction is impractical here, so this only checks that the
     * URL accepts a websocket.
     */
    async _websocketUrlConnects(wsEndpoint, wsUrl) {
        return new Promise(resolve => {
            // If we do not connect in appropriate timeframe,
            // call it a failure and try the next websocket
            const testWs = new isomorphic_ws_1.default(wsUrl);
            this._trackProbe(wsEndpoint, testWs);
            let settled = false;
            const finish = (connected) => {
                if (settled) {
                    return;
                }
                settled = true;
                clearTimeout(timeoutFailure);
                this._untrackProbe(wsEndpoint, testWs);
                terminateWs(testWs);
                resolve(connected);
            };
            const timeoutFailure = setTimeout(() => {
                finish(false);
            }, WEBSOCKET_TIMEOUT_MS);
            testWs.onerror = () => {
                finish(false);
            };
            testWs.onopen = () => {
                finish(true);
            };
        }).catch(() => {
            return false;
        });
    }
    /**
     * Delay before reconnect attempt `attempt` (0-based).
     * Public so unit tests can stub a shorter schedule.
     */
    reconnectDelayMs(attempt) {
        // attempt 0 is the reconnect for the socket that just closed.
        if (attempt <= 0) {
            return 0;
        }
        const exponent = Math.min(attempt - 1, 16);
        return Math.min(WS_RECONNECT_BASE_DELAY_MS * 2 ** exponent, WS_RECONNECT_MAX_DELAY_MS);
    }
    /**
     * A deliberate close, or a pause, must not open another socket.
     * `autoReconnect` only suppresses reconnects, not the first connectWs.
     */
    _connectionAborted(wsEndpoint, isReconnect) {
        return (wsEndpoint.manuallyClosed ||
            (isReconnect && !wsEndpoint.autoReconnect));
    }
    _clearReconnectTimer(wsEndpoint) {
        if (typeof wsEndpoint._clearReconnectTimer === 'function') {
            wsEndpoint._clearReconnectTimer();
            return;
        }
        if (wsEndpoint._reconnectTimer !== undefined) {
            clearTimeout(wsEndpoint._reconnectTimer);
            wsEndpoint._reconnectTimer = undefined;
        }
    }
    _scheduleReconnect(wsEndpoint) {
        this._clearReconnectTimer(wsEndpoint);
        const attempt = wsEndpoint._reconnectAttempt ?? 0;
        const delay = this.reconnectDelayMs(attempt);
        wsEndpoint._reconnectAttempt = attempt + 1;
        const timer = setTimeout(() => {
            if (wsEndpoint._reconnectTimer === timer) {
                wsEndpoint._reconnectTimer = undefined;
            }
            void this._reconnectAfterDelay(wsEndpoint);
        }, delay);
        wsEndpoint._reconnectTimer = timer;
    }
    /**
     * Run one reconnect. Failures are caught here so a down Chronik does not
     * surface as an unhandled rejection from the socket's onclose handler.
     */
    async _reconnectAfterDelay(wsEndpoint) {
        if (this._connectionAborted(wsEndpoint, true)) {
            return;
        }
        try {
            await this.connectWs(wsEndpoint, true);
        }
        catch (err) {
            if (this._connectionAborted(wsEndpoint, true)) {
                return;
            }
            emitWsError(wsEndpoint, err);
            if (this._connectionAborted(wsEndpoint, true)) {
                return;
            }
            this._scheduleReconnect(wsEndpoint);
        }
    }
    // Iterates through available websocket urls and attempts connection.
    // Upon a successful connection it handles the various websocket callbacks.
    // Upon an unsuccessful connection it iterates to the next websocket url in the array.
    // isReconnect is set for backoff retries. The first connect still runs when
    // autoReconnect is false; that flag only skips later attempts.
    async connectWs(wsEndpoint, isReconnect = false) {
        // An explicit connect (waitForOpen / resume) cancels a pending backoff.
        // If that connect cannot reach any endpoint, the backoff is re-armed
        // below so auto-reconnect does not stop.
        const hadPendingRetry = wsEndpoint._reconnectTimer !== undefined;
        this._clearReconnectTimer(wsEndpoint);
        for (let i = 0; i < this._endpointArray.length; i += 1) {
            if (this._connectionAborted(wsEndpoint, isReconnect)) {
                throw new Error('Chronik websocket connection aborted');
            }
            const index = this.deriveEndpointIndex(i);
            const thisProxyWsUrl = this._endpointArray[index].wsUrl;
            const websocketUrlConnects = await this._websocketUrlConnects(wsEndpoint, thisProxyWsUrl);
            if (!websocketUrlConnects) {
                continue;
            }
            if (this._connectionAborted(wsEndpoint, isReconnect)) {
                throw new Error('Chronik websocket connection aborted');
            }
            // Set this index to state
            this._workingIndex = index;
            const ws = new isomorphic_ws_1.default(thisProxyWsUrl);
            // Assign before handlers so a synchronous close is not treated
            // as a stale socket.
            wsEndpoint.ws = ws;
            let settled = false;
            let openTimeout;
            const clearOpenTimeout = () => {
                if (openTimeout !== undefined) {
                    clearTimeout(openTimeout);
                    openTimeout = undefined;
                }
            };
            const connected = new Promise((resolve, reject) => {
                const rejectConnected = (err) => {
                    if (settled) {
                        return;
                    }
                    settled = true;
                    clearOpenTimeout();
                    reject(err);
                };
                const resolveConnected = (msg) => {
                    if (settled) {
                        return;
                    }
                    settled = true;
                    clearOpenTimeout();
                    wsEndpoint._reconnectAttempt = 0;
                    // Send-only restore of this.subs, including plugins
                    wsEndpoint._resubscribeAll();
                    resolve(msg);
                    if (wsEndpoint.onConnect !== undefined) {
                        wsEndpoint.onConnect(msg);
                    }
                    // If no errors thrown from above call then set this index to state
                    this._workingIndex = index;
                };
                openTimeout = setTimeout(() => {
                    rejectConnected(new Error('Timed out connecting to Chronik websocket'));
                    terminateWs(ws);
                }, this._wsOpenTimeoutMs);
                ws.onopen = (msg) => {
                    resolveConnected(msg);
                };
                ws.onmessage = (e) => {
                    // handleMsg is async. Catch so a thrown application
                    // callback is not an unhandled rejection. Decode failures
                    // close the socket inside handleMsg; a callback error
                    // must leave it open.
                    wsEndpoint.handleMsg(e, ws).catch(err => {
                        console.error('Chronik websocket message handler failed:', err);
                    });
                };
                // Report the error and leave the socket up. close() would set
                // manuallyClosed and disable reconnect for every app that logs
                // onError. onclose decides whether to reconnect.
                ws.onerror = (event) => {
                    if (wsEndpoint.onError === undefined) {
                        return;
                    }
                    try {
                        wsEndpoint.onError(event);
                    }
                    catch (callbackErr) {
                        console.error('Chronik websocket onError callback failed:', callbackErr);
                    }
                };
                ws.onclose = (e) => {
                    clearOpenTimeout();
                    if (!settled) {
                        rejectConnected(new Error('Chronik websocket closed before it opened'));
                    }
                    // A replaced socket must not reconnect or reject the new
                    // attempt. Its own promise was already settled above.
                    if (wsEndpoint.ws !== undefined && wsEndpoint.ws !== ws) {
                        return;
                    }
                    // End if manually closed or no auto-reconnect
                    if (this._connectionAborted(wsEndpoint, true)) {
                        if (wsEndpoint.onEnd !== undefined) {
                            wsEndpoint.onEnd(e);
                        }
                        return;
                    }
                    if (wsEndpoint.onReconnect !== undefined) {
                        wsEndpoint.onReconnect(e);
                    }
                    // close() or pause() from onReconnect cancels the retry.
                    if (this._connectionAborted(wsEndpoint, true)) {
                        return;
                    }
                    this._workingIndex =
                        (this._workingIndex + 1) % this._endpointArray.length;
                    this._scheduleReconnect(wsEndpoint);
                };
            });
            // A later retry may reject this with nobody awaiting it.
            // The handler marks that rejection handled; waitForOpen still sees it.
            void connected.catch(() => undefined);
            wsEndpoint.connected = connected;
            return;
        }
        // If no websocket URLs connect, throw error
        const err = new Error(`Error connecting to known Chronik websockets`);
        if (hadPendingRetry &&
            !isReconnect &&
            !this._connectionAborted(wsEndpoint, true)) {
            emitWsError(wsEndpoint, err);
            this._scheduleReconnect(wsEndpoint);
        }
        throw err;
    }
}
exports.FailoverProxy = FailoverProxy;
//# sourceMappingURL=failoverProxy.js.map